import axios from "axios";
import { baseUrl } from "../../../apis/util";

const getConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
  },
});

// ==================== Board Members ====================

export const fetchBoardMembers = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-members/${queryString}`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching board members:", error);
    throw error;
  }
};

export const createBoardMember = async (data) => {
  try {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;
    
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        // Don't set Content-Type for FormData - browser sets it with boundary
      },
    };

    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error creating board member:", error);
    throw error;
  }
};

export const updateBoardMember = async (id, data) => {
  try {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;
    
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        // Don't set Content-Type for FormData - browser sets it with boundary
      },
    };

    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-members/${id}/`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error updating board member:", error);
    throw error;
  }
};

export const deleteBoardMember = async (id) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/board-members/${id}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting board member:", error);
    throw error;
  }
};

export const fetchBoardStats = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-members/stats/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching board stats:", error);
    throw error;
  }
};

export const recordAttendance = async (id, attendanceData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/${id}/attendance/`,
      attendanceData,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error recording attendance:", error);
    throw error;
  }
};

// ==================== Board Member Access Control ====================

/**
 * Get active board member count (for first member disclaimer logic)
 */
export const getActiveBoardMemberCount = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-members/count/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching board member count:", error);
    throw error;
  }
};

/**
 * Invite a user to become a board member
 * Requires Access Manager permission
 */
export const inviteBoardMember = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/invite/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error inviting board member:", error);
    throw error;
  }
};

/**
 * Get pending board member approvals (Governance Approvals in BoardActionsPanel).
 * Backend alias: `GET /api/admin-dash/pending-approvals/` hits the same view.
 * Payload: `profile_image` (avatar URL), `requested_at` ISO (same as invited_at when applicable).
 */
export const getPendingApprovals = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-members/pending/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching pending approvals:", error);
    throw error;
  }
};

/**
 * Approve a pending board member
 * Requires Board Member permission
 */
export const approveBoardMember = async (memberId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/${memberId}/approve/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error approving board member:", error);
    throw error;
  }
};

/**
 * Reject a pending board member
 * Requires Board Member permission
 */
export const rejectBoardMember = async (memberId, data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/${memberId}/reject/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error rejecting board member:", error);
    throw error;
  }
};

/**
 * Deactivate/remove a board member (immediate access revocation)
 * Requires Board Member or Access Manager permission
 */
export const deactivateBoardMember = async (memberId, data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/${memberId}/deactivate/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error deactivating board member:", error);
    throw error;
  }
};

/**
 * Reactivate a previously deactivated board member
 * Requires Board Member permission
 */
export const reactivateBoardMember = async (memberId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-members/${memberId}/reactivate/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error reactivating board member:", error);
    throw error;
  }
};

// ==================== Committees ====================

export const fetchCommittees = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/committees/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching committees:", error);
    throw error;
  }
};

export const fetchCommitteeDetails = async (id) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/committees/${id}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching committee details:", error);
    throw error;
  }
};

export const createCommittee = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/committees/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error creating committee:", error);
    throw error;
  }
};

export const updateCommittee = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/committees/${id}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating committee:", error);
    throw error;
  }
};

export const deleteCommittee = async (id) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/committees/${id}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting committee:", error);
    throw error;
  }
};

export const addCommitteeMember = async (committeeId, memberData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/committees/${committeeId}/members/`,
      memberData,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error adding committee member:", error);
    throw error;
  }
};

export const removeCommitteeMember = async (committeeId, memberId) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/committees/${committeeId}/members/${memberId}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error removing committee member:", error);
    throw error;
  }
};

// ==================== Events ====================

export const fetchEvents = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching events:", error);
    throw error;
  }
};

export const fetchEventDetails = async (id) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/${id}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching event details:", error);
    throw error;
  }
};

export const createEvent = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/events/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error creating event:", error);
    throw error;
  }
};

export const updateEvent = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/events/${id}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating event:", error);
    throw error;
  }
};

export const deleteEvent = async (id) => {
  try {
    await axios.delete(`${baseUrl}/api/admin-dash/events/${id}/`, getConfig());
  } catch (error) {
    console.error("Error deleting event:", error);
    throw error;
  }
};

export const fetchEventsForCalendar = async (year) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/calendar/${year || ""}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching calendar events:", error);
    throw error;
  }
};

export const fetchEventsByQuarter = async (quarter) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/by-quarter/${quarter}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching events by quarter:", error);
    throw error;
  }
};

export const fetchEventsByMonth = async (month) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/by-month/${month}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching events by month:", error);
    throw error;
  }
};

// ==================== Documents ====================

export const fetchDocuments = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/${queryString}`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

export const uploadDocument = async (formData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/documents/upload/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          // Don't set Content-Type for FormData - browser sets it with boundary
        },
      }
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error uploading document:", error);
    throw error;
  }
};

export const updateDocument = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/documents/${id}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

export const deleteDocument = async (id) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/documents/${id}/delete/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

export const downloadDocument = async (id) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/${id}/download/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error downloading document:", error);
    throw error;
  }
};

/**
 * Download board document as Blob (for clients that need binary stream).
 * GET /api/admin-dash/documents/{id}/download/
 */
export const downloadDocumentBlob = async (id) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/${id}/download/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error downloading document (blob):", error);
    throw error;
  }
};

/** GET /api/admin-dash/documents/{id}/notes/ → { data: [{ id, author, content, created_at }] } */
export const fetchDocumentNotes = async (documentId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/${documentId}/notes/`,
      getConfig()
    );
    const raw = response.data?.data ?? response.data;
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    console.error("Error fetching document notes:", error);
    throw error;
  }
};

/** POST /api/admin-dash/documents/{id}/notes/ body: { content } */
export const createDocumentNote = async (documentId, content) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/documents/${documentId}/notes/`,
      { content },
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating document note:", error);
    throw error;
  }
};

/** DELETE /api/admin-dash/documents/{id}/notes/{noteId}/ */
export const deleteDocumentNote = async (documentId, noteId) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/documents/${documentId}/notes/${noteId}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting document note:", error);
    throw error;
  }
};

/**
 * POST /api/admin-dash/documents/{id}/share/
 * body: { email, scope: "internal"|"external", expires_in_days?: number }
 */
export const shareDocument = async (documentId, payload) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/documents/${documentId}/share/`,
      payload,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error sharing document:", error);
    throw error;
  }
};

/** GET /api/admin-dash/documents/{id}/signatures/ — Penneo / provider stub */
export const fetchDocumentSignatures = async (documentId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/${documentId}/signatures/`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching document signatures:", error);
    throw error;
  }
};

/** POST /api/admin-dash/documents/{id}/signatures/remind/ — 503 until provider configured */
export const remindDocumentSignatures = async (documentId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/documents/${documentId}/signatures/remind/`,
      {},
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error sending signature reminder:", error);
    throw error;
  }
};

export const fetchDocumentsByQuarter = async (quarter) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/documents/by-quarter/${quarter}/`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching documents by quarter:", error);
    throw error;
  }
};

// ==================== KPIs ====================

export const fetchKPIs = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/kpis/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching KPIs:", error);
    throw error;
  }
};

export const createKPI = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/kpis/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error creating KPI:", error);
    throw error;
  }
};

export const updateKPI = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/kpis/${id}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating KPI:", error);
    throw error;
  }
};

export const deleteKPI = async (id) => {
  try {
    await axios.delete(`${baseUrl}/api/admin-dash/kpis/${id}/`, getConfig());
  } catch (error) {
    console.error("Error deleting KPI:", error);
    throw error;
  }
};

// ==================== Milestones ====================

export const fetchMilestones = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/milestones/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching milestones:", error);
    throw error;
  }
};

export const createMilestone = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/milestones/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error creating milestone:", error);
    throw error;
  }
};

export const updateMilestone = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/milestones/${id}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating milestone:", error);
    throw error;
  }
};

export const deleteMilestone = async (id) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/milestones/${id}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting milestone:", error);
    throw error;
  }
};

// ==================== Board Chemistry ====================

export const fetchChemistryAssessments = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-chemistry/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chemistry assessments:", error);
    throw error;
  }
};

export const submitChemistryAssessment = async (assessmentData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-chemistry/assessment/`,
      assessmentData,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting assessment:", error);
    throw error;
  }
};

export const fetchChemistryTrends = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-chemistry/trends/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chemistry trends:", error);
    throw error;
  }
};

export const fetchChemistryNotifications = async (boardMemberId) => {
  try {
    const config = {
      ...getConfig(),
      params: { board_member_id: boardMemberId },
    };
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-chemistry/notifications/`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chemistry notifications:", error);
    throw error;
  }
};

/**
 * NEW: Fetch annual wheel events data for visualization
 * Returns events grouped by month (keys: "1"-"12")
 */
export const fetchAnnualWheel = async (year) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/events/calendar/${year}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching annual wheel:", error);
    throw error;
  }
};

/**
 * NEW: Fetch board performance metrics for specific quarter
 * Defaults to current quarter if not specified
 */
export const fetchBoardPerformance = async (quarter = null, year = null) => {
  try {
    const params = {};
    if (quarter) params.quarter = quarter;
    if (year) params.year = year;

    const config = {
      ...getConfig(),
      params,
    };

    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-performance/`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching board performance:", error);
    throw error;
  }
};

/**
 * NEW: Fetch all executive roles for org chart
 */
export const fetchExecutives = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/executives/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching executives:", error);
    throw error;
  }
};

/**
 * NEW: Create new executive role
 */
export const createExecutive = async (executiveData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/executives/`,
      executiveData,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error creating executive:", error);
    throw error;
  }
};

/**
 * NEW: Fetch all governance frameworks
 * Returns object keyed by framework name (not array!)
 */
export const fetchGovernanceFrameworks = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/governance-frameworks/list/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching governance frameworks:", error);
    throw error;
  }
};

// ==================== Board Actions ====================

/**
 * Fetch personal board actions for the current user.
 * GET /api/admin-dash/board-actions/my/
 *
 * Rows may include optional deep-link fields (nullable numbers):
 * `meeting_id`, `chemistry_notification_id`, `attention_item_id`, `pulse_request_id`.
 * See `src/app/BusinessDash/types/boardAction.types.js` and BoardActionsPanel routing.
 */
export const fetchBoardActions = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-actions/my/`,
      getConfig()
    );
    const raw =
      response.data?.data ?? response.data?.results ?? response.data;
    return Array.isArray(raw) ? raw : [];
  } catch (error) {
    console.error("Error fetching board actions:", error);
    throw error;
  }
};

/**
 * Mark a board action as completed.
 * PATCH /api/admin-dash/board-actions/{id}/complete/
 */
export const markBoardActionComplete = async (actionId) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-actions/${actionId}/complete/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error marking board action complete:", error);
    throw error;
  }
};

// ==================== Governance Profile ====================

/**
 * Fetch the current board's governance profile preference.
 * GET /api/admin-dash/governance-profile/
 * Expected response: { profile_id: "standard" | "foundation" | "advanced" | "elite" }
 */
export const fetchGovernanceProfile = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/governance-profile/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching governance profile:", error);
    throw error;
  }
};

/**
 * Update the board's governance profile preference.
 * PATCH /api/admin-dash/governance-profile/
 * Body: { profile_id: string }
 */
export const updateGovernanceProfile = async (profileId) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/governance-profile/`,
      { profile_id: profileId },
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating governance profile:", error);
    throw error;
  }
};

// ==================== Pulse Requests ====================

/**
 * Fetch pending governance pulse requests (chair-only).
 * GET /api/admin-dash/pulse-requests/
 */
export const fetchPulseRequests = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/pulse-requests/`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching pulse requests:", error);
    throw error;
  }
};

/**
 * Record a chair decision on a governance pulse request.
 * POST /api/admin-dash/pulse-requests/{id}/decide/
 * {id} may be numeric pk (e.g. 3) or string slug (e.g. pulse-req-003); invalid id → 400.
 * Body: { decision: "approved" | "postponed" | "declined", postpone_days?: number }
 */
export const decidePulseRequest = async (requestId, decision, postponeDays) => {
  try {
    const payload = { decision };
    if (postponeDays) payload.postpone_days = postponeDays;
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/pulse-requests/${encodeURIComponent(String(requestId))}/decide/`,
      payload,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error deciding pulse request:", error);
    throw error;
  }
};

/**
 * List Governance Pulse assessments (same contract as board-chemistry list).
 * GET /api/admin-dash/governance-pulse/?ordering=-assessment_date&limit=1
 * Response: { data: [], count: number }
 */
export const fetchGovernancePulseAssessments = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/governance-pulse/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching governance pulse assessments:", error);
    throw error;
  }
};

/**
 * Submit Governance Pulse self-assessment (JWT user; no board_member_id in body).
 * POST /api/admin-dash/governance-pulse/assessment/
 * Body mirrors chemistry: answers, total_score, *_score dimensions, optional notes,
 * optional pulse_request_id (numeric or pulse-req-NNN).
 */
export const submitGovernancePulseAssessment = async (assessmentData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/governance-pulse/assessment/`,
      assessmentData,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting governance pulse assessment:", error);
    throw error;
  }
};

/**
 * Board training placeholder catalog (real catalog later).
 * GET /api/admin-dash/board-training/
 * Response: { stats: { completed_modules, total_modules, hours_logged, certificates_earned }, modules: [], courses: [] }
 */
export const fetchBoardTraining = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-training/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching board training:", error);
    throw error;
  }
};

/**
 * Chair Brief banner contract (chair/admin).
 * GET /api/admin-dash/chair-brief/
 * Default: { visible: false, title, summary, highlights, pdf_export_available }
 */
export const fetchChairBrief = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/chair-brief/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching chair brief:", error);
    throw error;
  }
};

/**
 * Download Chair Brief PDF (Option A — synchronous file).
 * GET /api/admin-dash/chair-brief/pdf/?brief_id= optional (reserved for future).
 * 200: application/pdf + Content-Disposition attachment.
 * 503: JSON { detail } (parsed from blob when using responseType blob).
 */
export const downloadChairBriefPdf = async (options = {}) => {
  const { briefId } = options;
  const params = {};
  if (briefId != null && briefId !== "") {
    params.brief_id = briefId;
  }

  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/chair-brief/pdf/`,
      {
        ...getConfig(),
        responseType: "blob",
        params,
      }
    );

    const blob = response.data;
    const contentType = (
      response.headers["content-type"] ||
      response.headers["Content-Type"] ||
      ""
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      const text = await blob.text();
      let detail = text;
      try {
        detail = JSON.parse(text)?.detail ?? text;
      } catch {
        /* use text */
      }
      throw new Error(typeof detail === "string" ? detail : "PDF generation failed.");
    }

    let filename = "chair-brief.pdf";
    const cd =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      "";
    if (cd) {
      const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
      const quoted = /filename="([^"]+)"/i.exec(cd);
      const plain = /filename=([^;\s]+)/i.exec(cd);
      if (utf8) {
        filename = decodeURIComponent(utf8[1]);
      } else if (quoted) {
        filename = quoted[1];
      } else if (plain) {
        filename = plain[1].replace(/^["']|["']$/g, "");
      }
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const res = error.response;
    if (res?.data instanceof Blob) {
      const text = await res.data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.detail === "string") {
          throw new Error(parsed.detail);
        }
      } catch (e) {
        if (e instanceof SyntaxError) {
          /* not JSON */
        } else {
          throw e;
        }
      }
      throw new Error(text.trim() || "PDF download failed.");
    }
    const detail = res?.data?.detail;
    if (typeof detail === "string") {
      throw new Error(detail);
    }
    console.error("Error downloading chair brief PDF:", error);
    throw new Error(error.message || "PDF download failed.");
  }
};

// ==================== Board Meetings ====================

export const fetchBoardMeetings = async (year) => {
  try {
    const params = year ? `?year=${year}` : "";
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-meetings/${params}`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching board meetings:", error);
    throw error;
  }
};

export const fetchBoardMeeting = async (id) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-meetings/${id}/`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching board meeting:", error);
    throw error;
  }
};

export const createBoardMeeting = async (data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/`,
      data,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating board meeting:", error);
    throw error;
  }
};

export const updateBoardMeeting = async (id, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-meetings/${id}/`,
      data,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error updating board meeting:", error);
    throw error;
  }
};

export const deleteBoardMeeting = async (id) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/board-meetings/${id}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting board meeting:", error);
    throw error;
  }
};

export const updateMeetingAgendaMinutes = async (meetingId, agendaId, minutes) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/agenda/${agendaId}/`,
      { minutes },
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating agenda minutes:", error);
    throw error;
  }
};

export const updateMeetingParticipant = async (meetingId, participantId, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/participants/${participantId}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating meeting participant:", error);
    throw error;
  }
};

export const uploadMeetingDocument = async (meetingId, formData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/documents/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error uploading meeting document:", error);
    throw error;
  }
};

export const addMeetingDocumentNote = async (meetingId, docId, content) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/documents/${docId}/notes/`,
      { content },
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error adding document note:", error);
    throw error;
  }
};

export const deleteMeetingDocumentNote = async (meetingId, docId, noteId) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/documents/${docId}/notes/${noteId}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting document note:", error);
    throw error;
  }
};

export const uploadMeetingFollowUpDoc = async (meetingId, formData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/followup-docs/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error uploading follow-up document:", error);
    throw error;
  }
};

export const addFollowUpDocNote = async (meetingId, docId, content) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/followup-docs/${docId}/notes/`,
      { content },
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error adding follow-up doc note:", error);
    throw error;
  }
};

export const deleteFollowUpDocNote = async (meetingId, docId, noteId) => {
  try {
    await axios.delete(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/followup-docs/${docId}/notes/${noteId}/`,
      getConfig()
    );
  } catch (error) {
    console.error("Error deleting follow-up doc note:", error);
    throw error;
  }
};

export const createMeetingTodo = async (meetingId, data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/todos/`,
      {
        task:      data.task,
        assignee:  data.assignee,
        due_date:  data.dueDate,
      },
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating meeting todo:", error);
    throw error;
  }
};

export const updateMeetingTodo = async (meetingId, todoId, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/todos/${todoId}/`,
      data,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error updating meeting todo:", error);
    throw error;
  }
};

export const createMeetingQuestion = async (meetingId, question) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/questions/`,
      { question },
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating meeting question:", error);
    throw error;
  }
};

export const uploadMeetingPhoto = async (meetingId, formData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/photos/`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error uploading meeting photo:", error);
    throw error;
  }
};

export const sendMinutesForApproval = async (meetingId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/minutes/send-for-approval/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error sending minutes for approval:", error);
    throw error;
  }
};

export const restartMinutesSigning = async (meetingId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/minutes/restart-signing/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error restarting minutes signing:", error);
    throw error;
  }
};

export const signMeetingMinutes = async (meetingId) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/minutes/sign/`,
      {},
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error signing meeting minutes:", error);
    throw error;
  }
};

// ==================== Board Meeting Decisions ====================

export const fetchMeetingDecisions = async (meetingId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/decisions/`,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error fetching meeting decisions:", error);
    throw error;
  }
};

export const createMeetingDecision = async (meetingId, data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/decisions/`,
      data,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error creating meeting decision:", error);
    throw error;
  }
};

export const updateMeetingDecision = async (meetingId, decisionId, data) => {
  try {
    const response = await axios.patch(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/decisions/${decisionId}/`,
      data,
      getConfig()
    );
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error("Error updating meeting decision:", error);
    throw error;
  }
};

export const deleteMeetingDecision = async (meetingId, decisionId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/decisions/${decisionId}/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting meeting decision:", error);
    throw error;
  }
};

// ==================== Strategy Data ====================

export const fetchStrategySegments = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/strategy/segments/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching strategy segments:", error);
    throw error;
  }
};

export const fetchAttentionItems = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/strategy/attention-items/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching attention items:", error);
    throw error;
  }
};

export const fetchBoardSignals = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/strategy/board-signals/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching board signals:", error);
    throw error;
  }
};

export const fetchUserStrategyActions = async () => {
  try {
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/strategy/my-actions/`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching user strategy actions:", error);
    throw error;
  }
};

export const recordBoardOutcome = async (meetingId, itemId, data) => {
  try {
    const response = await axios.post(
      `${baseUrl}/api/admin-dash/board-meetings/${meetingId}/outcomes/`,
      { attention_item_id: itemId, ...data },
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error recording board outcome:", error);
    throw error;
  }
};

// ==================== Attendance Records ====================

export const fetchAttendanceRecords = async (filters = {}) => {
  try {
    let queryString = "";
    if (Object.keys(filters).length > 0) {
      queryString = "?" + new URLSearchParams(filters).toString();
    }
    const response = await axios.get(
      `${baseUrl}/api/admin-dash/attendance-records/${queryString}`,
      getConfig()
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching attendance records:", error);
    throw error;
  }
};
